/**
 * Phase COI-REFRESH (Part B) — tenant settings card for the 5 refresh
 * knobs. Super-admin only (backend gates PATCH; FE hides the card for
 * non-super-admins).
 *
 * Reads:  GET  /api/admin/coi/refresh-settings → { settings, defaults }
 * Writes: PATCH /api/admin/coi/refresh-settings body { key: int }
 *
 * All 5 knobs are 1..365 day integers. Client-side validation catches
 * empty / non-positive / out-of-range inputs so the server never sees
 * garbage — the backend still validates independently.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ShieldAlert, RotateCcw } from "lucide-react";

const KNOBS = [
    {
        key: "days_before_expiry",
        label: "Days before expiry to send refresh",
        help: "Auto-send a fresh COI request this many days before the on-file COI expires.",
        min: 1, max: 365,
    },
    {
        key: "cadence_days",
        label: "Cadence — days since last approval",
        help: "Force a fresh COI request every N days since the last one was approved, even if it hasn't expired yet.",
        min: 1, max: 365,
    },
    {
        key: "grace_days_admin_notify",
        label: "Grace — admin card (days past expiry)",
        help: "How many days after expiry to raise the admin bell-card so someone follows up.",
        min: 1, max: 365,
    },
    {
        key: "grace_days_customer_notify",
        label: "Grace — customer branded email (days past expiry)",
        help: "How many days after expiry to send the branded lapse email to the customer contact.",
        min: 1, max: 365,
    },
    {
        key: "grace_days_lapse_flag",
        label: "Grace — stamp insurance_lapsed flag (days past expiry)",
        help: "How many days after expiry to flag the rental as lapsed for dashboard filtering.",
        min: 1, max: 365,
    },
];

function parseInputInt(v) {
    if (v === "" || v == null) return null;
    const n = parseInt(String(v), 10);
    return Number.isFinite(n) ? n : NaN;
}

export default function CoiRefreshCard({ isSuperAdmin }) {
    const [settings, setSettings] = useState(null);
    const [defaults, setDefaults] = useState(null);
    const [form, setForm] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/coi/refresh-settings");
            setSettings(data?.settings || {});
            setDefaults(data?.defaults || {});
            setForm({ ...(data?.settings || {}) });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const onChange = (k, v) => {
        setForm((prev) => ({ ...prev, [k]: v }));
    };

    const validate = () => {
        for (const k of KNOBS) {
            const v = parseInputInt(form[k.key]);
            if (v == null || Number.isNaN(v) || v < k.min || v > k.max) {
                return `${k.label}: enter a whole number between ${k.min} and ${k.max}.`;
            }
        }
        return null;
    };

    const save = async () => {
        setError("");
        const err = validate();
        if (err) { setError(err); return; }
        setSaving(true);
        try {
            const patch = {};
            for (const k of KNOBS) {
                const cur = parseInputInt(form[k.key]);
                if (cur !== settings?.[k.key]) patch[k.key] = cur;
            }
            if (Object.keys(patch).length === 0) {
                toast.info("No changes to save.");
                setSaving(false);
                return;
            }
            const { data } = await api.patch("/admin/coi/refresh-settings", patch);
            setSettings(data?.settings || {});
            setForm({ ...(data?.settings || {}) });
            toast.success("COI refresh settings saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const resetToDefaults = () => {
        if (!defaults) return;
        setForm({ ...defaults });
    };

    if (!isSuperAdmin) return null;  // Card is entirely hidden for non-super-admins.

    return (
        <Card data-testid="settings-coi-refresh-card">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <ShieldAlert size={16} className="text-blue-900" />
                    <CardTitle className="font-display text-xl">COI Refresh (Phase COI-REFRESH)</CardTitle>
                </div>
                <CardDescription>
                    Automation knobs for the daily COI verification cron. Fresh COI requests fire before expiry or on a fixed cadence, and post-expiry grace-period actions surface in the admin bell + customer email.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <p className="text-sm text-slate-500">Loading…</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                        {KNOBS.map((k) => (
                            <div key={k.key} className="space-y-1.5">
                                <Label htmlFor={`coi-refresh-${k.key}`} className="text-sm">
                                    {k.label}
                                </Label>
                                <div className="flex items-center gap-2">
                                    <Input
                                        id={`coi-refresh-${k.key}`}
                                        type="number"
                                        min={k.min}
                                        max={k.max}
                                        step={1}
                                        value={form[k.key] ?? ""}
                                        onChange={(e) => onChange(k.key, e.target.value)}
                                        className="w-24"
                                        disabled={saving}
                                        data-testid={`settings-coi-refresh-${k.key}-input`}
                                    />
                                    <span className="text-xs text-slate-500">days</span>
                                    {defaults && defaults[k.key] != null && (
                                        <span className="text-[11px] text-slate-400">
                                            default {defaults[k.key]}
                                        </span>
                                    )}
                                </div>
                                <p className="text-xs text-slate-500 leading-snug">{k.help}</p>
                            </div>
                        ))}
                    </div>
                )}
                {error && (
                    <div className="mt-4 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-100"
                        data-testid="settings-coi-refresh-error">
                        {error}
                    </div>
                )}
                <div className="mt-5 flex items-center justify-between gap-3">
                    <button
                        type="button"
                        onClick={resetToDefaults}
                        disabled={saving || loading || !defaults}
                        className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-blue-900 underline-offset-2 hover:underline disabled:opacity-40"
                        data-testid="settings-coi-refresh-reset"
                    >
                        <RotateCcw size={11} />
                        Reset to defaults
                    </button>
                    <Button
                        onClick={save}
                        disabled={saving || loading}
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="settings-coi-refresh-save"
                    >
                        {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            </CardContent>
        </Card>
    );
}
